/**
 * focusRegistry.js — Everything the Focus switch can hide, by key.
 *
 * Nav keys are derived from paths so the sidebar, the mobile tab bar and the
 * Focus-set editor agree without a list to keep in sync:
 *   '/'              → nav.home
 *   '/payroll'       → nav.payroll
 *   Payroll ▸ Company Accounting (?section=company) → nav.payroll.company
 *
 * In-screen controls are listed in FOCUS_ELEMENTS and wrapped with <FocusHide k=…>
 * (or checked with useFocus().isHidden). Every hideable element carries
 * data-focus={key}; click telemetry records it, so each key's real use is countable.
 */

export function navKey(path) {
  if (!path || path === '/') return 'nav.home';
  return `nav.${path.replace(/^\//, '').replace(/\//g, '.')}`;
}

/** Key for a sidebar flyout target (sub-tab, shortcut or sub-page). */
export function navSubKey(itemPath, target) {
  const leaf = target.value ?? String(target.path || '').split('/').pop();
  return `${navKey(itemPath)}.${leaf}`;
}

/**
 * In-screen controls the Focus switch can hide. `screen` groups them in the
 * Focus-set editor; `note` is the usage evidence (2026-10-11 element audit:
 * usage_events since 9/16, action_log, table data, backend journal).
 * `measurable: false` marks display-only items that clicks can't measure.
 */
export const FOCUS_ELEMENTS = [
  // ── Jobs ──
  { key: 'job.logTime', screen: 'Jobs', label: "Job detail 'Log Time / My Hours' + logged-hours readout (planned hours stay)", note: 'time_entries has 0 rows; pay runs from the calc snapshot' },
  { key: 'job.logExpenses', screen: 'Jobs', label: "Job detail 'Log Expenses'", note: 'No expense has ever been linked to a job' },
  { key: 'job.handoffNotes', screen: 'Jobs', label: "Job detail 'Handoff Notes' card", note: '6 notes on 3 jobs lifetime, last 5/24' },
  { key: 'job.financials.extra', screen: 'Jobs', label: "Job Financials '+ Extra' invoice", note: '1 extra invoice ever (9/14)' },
  { key: 'job.financials.link', screen: 'Jobs', label: "Job Financials 'Link…' Xero invoice", note: '6 links lifetime, 1 since 9/16' },
  { key: 'jobs.inquiriesButton', screen: 'Jobs', label: "Jobs header 'Inquiries' button", note: '1 visit since 9/16; 5 web leads ever' },
  { key: 'jobs.xeroReconcile', screen: 'Jobs', label: 'Jobs toolbar Xero reconcile icon', note: '3 uses in ship week (9/9-9/12), 0 since' },
  { key: 'home.platePicker', screen: 'Home', label: "Home 'Plate of' user picker", note: 'Used on 7 days lifetime, last 9/30' },
  { key: 'home.drivesPill', screen: 'Home', label: "Home 'N drives need review' pill", note: 'No drive reviewed since 5/10' },

  // ── Quotes ──
  // The catalog picker (ServiceSelector: RE category tabs, VirTour packages) is
  // deliberately not hideable — a quote must always be able to offer the full
  // price list, in Focus or Full.
  { key: 'quote.saveAndSend', screen: 'Quotes', label: "Quote Builder '✉ Save & Send'", note: '0 uses; every quote send since July goes through Job Pipeline step 2' },
  { key: 'quote.re.propertySelects', screen: 'Quotes', label: 'RE Property Type / Bedrooms / Bathrooms', note: "Don't affect price; beds/baths 0 since 9/1" },
  { key: 'quote.internalNotes', screen: 'Quotes', label: "Quote Builder 'Internal Notes' card", note: 'Set on 5 quote chains ever, 0 since 9/16' },
  { key: 'quote.staycationToggle', screen: 'Quotes', label: "Quote Builder '✦ Staycation' toggle", note: 'Used on 1 quote ever (9/09)' },
  { key: 'quote.line.addDay', screen: 'Quotes', label: "Line item '+ Add Day'", note: '4 lines in 2 chains ever, last 8/21' },
  { key: 'quote.termEdit', screen: 'Quotes', label: 'Terms: per-term Edit / Reset / Save to library', note: 'No term override has ever been saved' },
  { key: 'quotes.paySplitTab', screen: 'Quotes', label: "Quotes 'Pay Split' tab", note: '0 saves from /quotes; every calculator visit comes from a job' },
  { key: 'quoteBrowser.actions', screen: 'Quotes', label: 'Saved Quotes: Mark Sent / Mark Accepted / Send / Duplicate / Reject', note: '0 uses; accepts and sends all happen on the job' },
  { key: 'quoteBrowser.delete', screen: 'Quotes', label: "Saved Quotes 'Delete'", note: 'Server requires quotes:full; crew would get a 403' },

  // ── Calculator ──
  { key: 'calc.itemSplit', screen: 'Calculator', label: "Per-item 'Split' button", note: 'itemSplits empty on every saved snapshot' },
  { key: 'calc.postAddCrew', screen: 'Calculator', label: "Job-level Post Production '+ Add Crew'", note: '0 snapshots with post hourly crew' },
  { key: 'calc.staycationSplit', screen: 'Calculator', label: "'✦ Staycation split' button", note: '1 job ever (7/02)' },
  { key: 'calc.newJob', screen: 'Calculator', label: "Standalone '+ New Job'", note: '0 jobs created from the calculator' },

  // ── Invoices ──
  { key: 'invoices.emptyCreate', screen: 'Invoices', label: "Empty-state '+ Create New Invoice'", note: '0 creates from /invoices; all come from job detail' },
  { key: 'invoices.new', screen: 'Invoices', label: "Invoice list '+ New'", note: 'Manual no-job invoice never used' },
  { key: 'invoices.overdueCard', screen: 'Invoices', label: "'Overdue' stat card", note: 'Open balance past due date (was always $0 until 2026-10-11)', measurable: false },
  { key: 'invoices.xeroChip', screen: 'Invoices', label: "'Xero Connected' chip", note: '0 clicks through to Settings' },
  { key: 'invoice.pdfButtons', screen: 'Invoices', label: "Invoice detail 'Preview' + 'Download PDF'", note: '0 PDF builds from /invoices since 9/16' },
  { key: 'invoice.markSent', screen: 'Invoices', label: "Invoice detail 'Mark Sent'", note: "No invoice has ever had status 'sent'" },
  { key: 'invoice.editMarkPaid', screen: 'Invoices', label: "Invoice detail 'Edit', payment 'Void', 'Mark Paid'", note: '0 uses from /invoices; paid transitions come from the job' },
  { key: 'invoice.xeroButtons', screen: 'Invoices', label: 'Invoice detail Push / Re-sync / Re-upload PDF to Xero', note: 'Pushes are automatic; re-upload has failed on all 164 calls' },
  { key: 'invoice.recordPayment', screen: 'Invoices', label: "Invoice detail 'Record Payment'", note: '1 manual payment ever (4/08)' },
  { key: 'invoice.delete', screen: 'Invoices', label: "Invoice detail 'Delete'", note: '0 since 9/16; needed invoices:full until manage_own (pay-split jobs) shipped' },

  // ── People / Calendar / How To ──
  { key: 'contact.interactions', screen: 'People', label: "Contact 'Interactions' card + '+ Log Interaction'", note: '0 interactions across all 1,187 contacts' },
  { key: 'contact.convertToClient', screen: 'People', label: "Contact 'Convert to Client'", note: 'Never used' },
  { key: 'contact.delete', screen: 'People', label: "Contact 'Delete'", note: '7 deletes, all April' },
  { key: 'contactForm.unusedFields', screen: 'People', label: 'Contact form Speciality / Tags / Follow-up / Core Crew', note: '0 non-empty values ever' },
  { key: 'calendar.addEvent', screen: 'Calendar', label: "Calendar '+ Event' (desktop + mobile)", note: 'calendar_events has 0 rows; scheduling goes through job GCal sync' },
  { key: 'howto.inspirationTab', screen: 'How To', label: "How To 'Inspiration' tab", note: 'All 7 articles are how-tos; the tab is always empty' },
  { key: 'howto.related', screen: 'How To', label: "How To detail 'More in …' strip", note: '0 of 9 detail views came from it' },

  // ── Open Houses ──
  { key: 'openhouse.runFollowup', screen: 'Open Houses', label: "'Run' + 'Follow-up' tabs and 'Start run'", note: '0 stops ever visited; every plan is still a draft' },
  { key: 'openhouse.browseMore', screen: 'Open Houses', label: "Browse 'More' menu items + listing Archive icon", note: 'Zillow enrich 4 calls on 7/03; import/add/archive never used' },

  // ── Settings / shell ──
  { key: 'settings.notificationsTab', screen: 'Settings', label: "Settings 'Notifications' tab", note: 'Never saved; no server code sends these emails' },
  { key: 'settings.deadControls', screen: 'Settings', label: "'Sidebar Default', 'Density'", note: 'Read by nothing' },
  { key: 'settings.comingSoon', screen: 'Settings', label: "Integrations 'Coming Soon' Gmail / Drive cards", note: 'Disabled stubs' },
  { key: 'settings.dataCsv', screen: 'Settings', label: "Data 'Download CSV' + 'Import'", note: 'All CSV export hits were scanner probes; import never wired' },
  { key: 'sidebar.serverStats', screen: 'Sidebar', label: 'Server + AI usage widget', note: 'Infra readout; shows company AI spend to every role' },
];

/**
 * Recommended Focus sets from the 2026-10-11 audit — high-confidence only:
 * measured zero (or broken) with a signal that would have seen use. Rare-but-used
 * items stay visible until click data says otherwise (review ~2026-11-01).
 * `all` applies to everyone; role keys add to it.
 */
export const FOCUS_RECOMMENDED = {
  all: [
    // Screens nobody works in any more
    'nav.outreach', 'nav.voicenotes', 'nav.projects', 'nav.calculator',
    // Never-used flyout shortcuts
    'nav.jobs.newJob', 'nav.quotes.calculator', 'nav.invoices.newInvoice', 'nav.calendar.addEvent',
    // In-screen controls with zero use
    'job.logTime', 'job.logExpenses', 'home.drivesPill',
    'quote.saveAndSend', 'quote.termEdit',
    'quotes.paySplitTab', 'quoteBrowser.actions',
    'calc.itemSplit', 'calc.postAddCrew', 'calc.newJob',
    'invoices.emptyCreate', 'invoices.new', 'invoices.xeroChip',
    'invoice.pdfButtons', 'invoice.markSent', 'invoice.editMarkPaid', 'invoice.xeroButtons',
    'contact.interactions', 'contact.convertToClient', 'contactForm.unusedFields',
    'calendar.addEvent', 'howto.inspirationTab', 'openhouse.runFollowup', 'openhouse.browseMore',
    'settings.notificationsTab', 'settings.deadControls', 'settings.comingSoon', 'settings.dataCsv',
  ],
  super_admin: ['nav.links', 'nav.vehicle', 'nav.legal', 'nav.rototube'],
  core_crew: [
    'nav.openhouse', 'nav.rototube', 'nav.settings', 'nav.jobs.inquiries',
    // 'invoice.delete' left the set: invoices:manage_own lets core crew delete
    // on their pay-split jobs, so the button is no longer a guaranteed 403.
    'sidebar.serverStats', 'quoteBrowser.delete',
  ],
};

export function recommendedFor(role) {
  return [...new Set([...FOCUS_RECOMMENDED.all, ...(FOCUS_RECOMMENDED[role] || [])])];
}
